import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import ArticleCard from "@/components/feature/ArticleCard";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = "http://cqxbygc.cn";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const pageTitle = category ? `${category.name} - 91吃瓜` : "分类 - 91吃瓜";
  const pageDesc = category
    ? `浏览${category.name}最新爆料与热门话题，${category.description}`
    : "浏览91吃瓜各分类最新爆料与热门话题";

  return (
    <>
      {/* SEO Meta via React 19 title */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <link rel="canonical" href={`/category/${slug}`} />

      {/* WebPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: pageTitle,
          description: pageDesc,
          url: `${SITE_URL}/category/${slug}`,
          isPartOf: {
            "@type": "WebSite",
            name: "91吃瓜社区",
            url: SITE_URL,
          },
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: SITE_URL,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category?.name || "分类",
              item: `${SITE_URL}/category/${slug}`,
            },
          ],
        })}
      </script>

      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-3 md:py-4">
          <nav aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors">
                  首页
                </Link>
              </li>
              <li>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </li>
              <li className="text-foreground-800 font-medium">
                {category?.name || "分类"}
              </li>
            </ol>
          </nav>
        </div>

        {/* Category Tabs - Quick Switch */}
        <section className="w-full px-4 md:px-6 lg:px-8 pt-2 pb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <span className="text-xs text-foreground-500 shrink-0 mr-1">切换分类：</span>
            {categories.map((c) => {
              const isActive = c.slug === slug;
              return (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-primary-500 text-white shadow-sm"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700 border border-background-200/60"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className={`w-4 h-4 flex items-center justify-center ${isActive ? "text-white/90" : "text-primary-500"}`}>
                    <i className={c.icon} />
                  </span>
                  {c.name}
                  <span className={`text-xs ${isActive ? "text-white/70" : "text-foreground-400"}`}>
                    {c.articleCount}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-6">
          <div className="bg-background-100 rounded-xl border border-background-200/60 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${category?.icon || "ri-folder-line"} text-lg md:text-xl`} />
              </span>
              <h1
                className="text-2xl md:text-3xl font-bold text-foreground-950"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {category?.name || "未知分类"}
              </h1>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              {category?.description || "浏览该分类下的最新爆料与热门话题"}
            </p>
            <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-article-line" />
                {categoryArticles.length} 篇文章
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                每日更新
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line" />
                {articles.filter((a) => a.categorySlug === slug && a.isHot).length} 篇热门
              </span>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-6">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} {...article} variant="featured" />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <span className="w-12 h-12 flex items-center justify-center mx-auto text-foreground-300">
                <i className="ri-inbox-line text-3xl" />
              </span>
              <p className="mt-3 text-foreground-500">该分类暂无文章</p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          )}
        </section>

        {/* Other Categories CTA */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="bg-background-100 rounded-xl border border-background-200/60 p-5 md:p-6">
            <h3 className="text-sm font-semibold text-foreground-950 mb-3">探索其他分类</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {categories
                .filter((c) => c.slug !== slug)
                .map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-50 text-foreground-700 text-xs font-medium hover:bg-primary-50 hover:text-primary-700 transition-colors border border-background-200/60"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center text-primary-500">
                      <i className={c.icon} />
                    </span>
                    {c.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
